export type FederatedWidgetMount = {
  widgetId: string;
  toolInput?: Record<string, unknown>;
  toolOutput: Record<string, unknown>;
};

function asObject(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

function parseMoney(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return undefined;
  const cleaned = value.replace(/[^0-9.-]/g, '');
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

function normalizeProductForFederated(raw: unknown): Record<string, unknown> | null {
  const obj = asObject(raw);
  if (!obj) return null;
  const next: Record<string, unknown> = { ...obj };

  // Concierge federated widgets expect numeric prices (they call `formatMoney`).
  // Voice widgets may carry string prices like "$19.99".
  const price = parseMoney(obj.retailPrice ?? obj.price);
  if (price !== undefined) {
    // Prefer `retailPrice` when present; set both defensively for older widgets.
    next.retailPrice = price;
    next.price = price;
  }

  const reward = parseMoney(obj.totalRewardAmount);
  if (reward !== undefined) next.totalRewardAmount = reward;

  return next;
}

function normalizeProductsForFederated(products: unknown[]): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  for (const p of products) {
    const normalized = normalizeProductForFederated(p);
    if (normalized) out.push(normalized);
  }
  return out;
}

export function mapVoiceWidgetToFederated(type: string, data: unknown): FederatedWidgetMount | null {
  const obj = asObject(data);
  if (!obj) return null;

  // `data-widget-product-card` -> `product-highlight`, the same single-product
  // card text mode shows. The widget loads the product itself, so it needs
  // only the sku.
  if (type === 'data-widget-product-card') {
    const stacklineSku = obj.stacklineSku;
    if (typeof stacklineSku !== 'string' || !stacklineSku) return null;
    return {
      widgetId: 'product-highlight',
      toolOutput: { stacklineSku },
    };
  }

  // `data-widget-comparison` is overloaded:
  // - compare_products emits `attributes` -> map to `compare-products` (`comparisonAttributes`).
  // - search_products / get_product_details emits only `products` -> map to `product-carousels` (one section).
  if (type === 'data-widget-comparison') {
    const products = obj.products;
    if (!Array.isArray(products) || products.length === 0) return null;
    const normalizedProducts = normalizeProductsForFederated(products);
    if (normalizedProducts.length === 0) return null;

    const attributes = obj.attributes;
    if (Array.isArray(attributes) && attributes.length > 0) {
      return {
        widgetId: 'compare-products',
        toolOutput: {
          products: normalizedProducts,
          comparisonAttributes: attributes,
        },
      };
    }

    return {
      widgetId: 'product-carousels',
      toolOutput: {
        sections: [
          {
            title: '',
            products: normalizedProducts,
          },
        ],
      },
    };
  }

  return null;
}

